import { useEffect } from 'react';
import type { WebsiteContent } from '../data/website';

const managedAttribute = 'data-artistos-seo';

export interface WebsiteSeoOverrides {
  title?: string;
  description?: string;
  shareTitle?: string;
  shareDescription?: string;
  shareImageUrl?: string;
  /** Appended to the site address to form this page's canonical URL. */
  canonicalPath?: string;
  ogType?: string;
  structuredData?: unknown;
  /** Keeps a draft preview out of search results. */
  noIndex?: boolean;
}

/** Applies title, meta, social preview, and structured data for a public page. */
export function useWebsiteSeo(content: WebsiteContent, overrides: WebsiteSeoOverrides = {}) {
  useEffect(() => {
    const title = overrides.title?.trim() || content.seoTitle?.trim() || `${content.bandName} · Official Site`;
    const description =
      overrides.description?.trim() ||
      content.seoDescription?.trim() ||
      `Tour dates, music, and merch from ${content.bandName}.`;
    const siteUrl = content.siteUrl?.trim().replace(/\/$/, '');
    const shareImage = overrides.shareImageUrl?.trim() || content.shareImageUrl?.trim() || content.heroImageUrl;
    const canonical = siteUrl ? `${siteUrl}${overrides.canonicalPath ?? ''}` : '';

    document.title = title;
    document.documentElement.lang = 'en';

    setMeta('name', 'description', description);
    if (overrides.noIndex) setMeta('name', 'robots', 'noindex, nofollow');
    setMeta('property', 'og:type', overrides.ogType ?? 'website');
    setMeta('property', 'og:title', overrides.shareTitle?.trim() || title);
    setMeta('property', 'og:description', overrides.shareDescription?.trim() || description);
    setMeta('property', 'og:site_name', content.bandName);
    if (shareImage) setMeta('property', 'og:image', shareImage);
    if (canonical) setMeta('property', 'og:url', canonical);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', overrides.shareTitle?.trim() || title);
    setMeta('name', 'twitter:description', overrides.shareDescription?.trim() || description);
    if (shareImage) setMeta('name', 'twitter:image', shareImage);

    if (canonical) setCanonical(canonical);
    setStructuredData(overrides.structuredData ?? buildStructuredData(content));

    return () => {
      document.head.querySelectorAll(`[${managedAttribute}]`).forEach((node) => node.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, JSON.stringify(overrides)]);
}

function setMeta(keyName: 'name' | 'property', key: string, value: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${keyName}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(keyName, key);
    tag.setAttribute(managedAttribute, 'true');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
}

function setCanonical(url: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    link.setAttribute(managedAttribute, 'true');
    document.head.appendChild(link);
  }
  link.href = url;
}

function setStructuredData(data: unknown) {
  let script = document.head.querySelector<HTMLScriptElement>(`script[${managedAttribute}="schema"]`);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(managedAttribute, 'schema');
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function buildStructuredData(content: WebsiteContent) {
  const socialLinks = [
    content.instagramUrl,
    content.tiktokUrl,
    content.spotifyUrl,
    content.youtubeUrl,
    content.musicSecondaryUrl,
  ].filter(Boolean);
  const events = content.showsHidden
    ? []
    : content.shows.map((show) => ({
        '@type': 'MusicEvent',
        name: `${content.bandName} at ${show.venue}`,
        startDate: toIsoDate(show.date),
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location: { '@type': 'Place', name: show.venue, address: show.city },
        performer: { '@type': 'MusicGroup', name: content.bandName },
        ...(show.ticketUrl && show.ticketUrl !== '#'
          ? { offers: { '@type': 'Offer', url: show.ticketUrl, availability: show.status === 'sold-out' ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock' } }
          : {}),
      }));

  return {
    '@context': 'https://schema.org',
    '@type': 'MusicGroup',
    name: content.bandName,
    description: content.seoDescription,
    genre: content.seoGenre,
    ...(content.siteUrl ? { url: content.siteUrl } : {}),
    ...(content.shareImageUrl ? { image: content.shareImageUrl } : {}),
    ...(content.seoLocation ? { foundingLocation: { '@type': 'Place', name: content.seoLocation } } : {}),
    ...(socialLinks.length ? { sameAs: socialLinks } : {}),
    ...(content.bookingEmail ? { email: content.bookingEmail } : {}),
    ...(events.length ? { event: events } : {}),
  };
}

function toIsoDate(displayDate: string): string {
  const parsed = new Date(displayDate);
  return Number.isNaN(parsed.getTime()) ? displayDate : parsed.toISOString().slice(0, 10);
}
